import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AdminFilterBar } from "@/components/admin/AdminFilterBar";
import { AdminDetailPanel } from "@/components/admin/AdminDetailPanel";
import { SPVerificationTab } from "@/components/admin/SPVerificationTab";
import { RejectedVerificationsTab } from "@/components/admin/RejectedVerificationsTab";
import {
  ShieldCheck, AlertTriangle, CheckCircle, XCircle, Clock,
  FileText, MapPin, Eye, Lock, Unlock, ChevronRight, Trash2, Search,
  Building2, Dumbbell
} from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  under_review: "bg-blue-100 text-blue-800",
  verified: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  suspended: "bg-slate-100 text-slate-800",
};
const LEVEL_NAMES = { 0: "Unverified", 1: "Document", 2: "Geo" };
const LEVEL_STATUS_STYLES = {
  approved: "bg-green-50 border-green-200 text-green-800",
  current: "bg-amber-50 border-amber-200 text-amber-800",
  pending: "bg-slate-50 border-slate-200 text-slate-600",
};

const resolveUploadUrl = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname.includes("/api/uploads/")) {
      return `${window.location.origin}${parsed.pathname}`;
    }
    return url;
  } catch {
    return url;
  }
};

const isImagePreview = (url) => /\.(jpg|jpeg|png|webp|heic)$/i.test(url || "");

export default function VenueVerification() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [allVenues, setAllVenues] = useState([]);
  const [counts, setCounts] = useState({});
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionReason, setActionReason] = useState("");
  const [actionLevel, setActionLevel] = useState("1");
  const [loading, setLoading] = useState(true);
  const [primaryTab, setPrimaryTab] = useState("venue");
  const [venueSubTab, setVenueSubTab] = useState("queue");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [businessTypeFilter, setBusinessTypeFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("latest");
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [spCount, setSpCount] = useState(0);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [qRes, allRes] = await Promise.all([
        api.get("/vov/admin/queue"),
        api.get("/vov/admin/all-venues"),
      ]);
      setQueue(qRes.data.queue || []);
      setCounts(qRes.data.counts || {});
      setAllVenues(allRes.data.venues || []);
    } catch { toast.error("Failed to load verification data"); }
    setLoading(false);
  };

  const allStatuses = ["all", "pending", "under_review", "verified", "rejected", "suspended"];
  const businessTypes = Array.from(new Set(allVenues.map((venue) => venue.business_type).filter(Boolean)));

  const filteredQueue = useMemo(() => {
    const rows = queue.filter((venue) => {
      const text = [venue.name, venue.city, venue.owner_name].filter(Boolean).join(" ").toLowerCase();
      const matchesSearch = !query || text.includes(query.toLowerCase());
      const matchesStatus = statusFilter === "all" || venue.ownership_verification_status === statusFilter;
      const matchesBusinessType = businessTypeFilter === "all" || venue.business_type === businessTypeFilter;
      return matchesSearch && matchesStatus && matchesBusinessType;
    });
    return rows.sort((a, b) => {
      const da = a.verification_submitted_at || a.created_at || "";
      const db2 = b.verification_submitted_at || b.created_at || "";
      return sortOrder === "latest" ? db2.localeCompare(da) : da.localeCompare(db2);
    });
  }, [queue, query, statusFilter, businessTypeFilter, sortOrder]);

  const filteredAllVenues = useMemo(() => {
    const rows = allVenues.filter((venue) => {
      const text = [venue.name, venue.owner_name, venue.owner_email, venue.city].filter(Boolean).join(" ").toLowerCase();
      const matchesSearch = !query || text.includes(query.toLowerCase());
      const matchesStatus = statusFilter === "all" || venue.status === statusFilter;
      const matchesBusinessType = businessTypeFilter === "all" || venue.business_type === businessTypeFilter;
      return matchesSearch && matchesStatus && matchesBusinessType;
    });
    return rows.sort((a, b) => {
      const da = a.created_at || "";
      const db2 = b.created_at || "";
      return sortOrder === "latest" ? db2.localeCompare(da) : da.localeCompare(db2);
    });
  }, [allVenues, query, statusFilter, businessTypeFilter, sortOrder]);

  const filteredVerified = filteredAllVenues.filter((venue) => venue.status === "verified");
  const rejectedVenues = allVenues.filter((venue) => venue.status === "rejected");

  const openDetail = async (venueId, openModal = true) => {
    try {
      const res = await api.get(`/vov/admin/venue/${venueId}`);
      setDetail(res.data);
      setActionLevel(String(res.data.current_review_level || 1));
      setDetailOpen(openModal);
      setActionReason("");
    } catch { toast.error("Failed to load venue details"); }
  };

  const takeAction = async (venueId, action) => {
    try {
      const res = await api.post(`/vov/admin/action/${venueId}`, {
        action, reason: actionReason, verification_level: parseInt(actionLevel),
      });
      if (action === "approve") {
        const nextLevel = res.data.next_review_level || parseInt(actionLevel, 10);
        toast.success(nextLevel > parseInt(actionLevel, 10) ? `Level ${actionLevel} approved. Continue with Level ${nextLevel}.` : "Venue approved successfully");
        await openDetail(venueId);
      } else {
        toast.success(`Venue ${action}d successfully`);
        setDetailOpen(false);
      }
      setActionReason("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  const deleteRejectedVenue = async (venueId) => {
    const confirmed = window.confirm("Delete this rejected venue permanently? This cannot be undone.");
    if (!confirmed) return;
    try {
      await api.delete(`/vov/admin/venue/${venueId}`);
      toast.success("Rejected venue deleted");
      if (selectedVenueId === venueId) setSelectedVenueId(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete venue");
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="venue-verification-page">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl" data-testid="sp-venue-verification-title">SP / Venue Verification</h1>
      </div>

      {/* Status Counts */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(counts).map(([k, v]) => (
          <Badge key={k} className={STATUS_STYLES[k] || "bg-muted"}>{k}: {v}</Badge>
        ))}
      </div>

      {/* Primary Tabs: Venue | SP | Rejected */}
      <Tabs value={primaryTab} onValueChange={setPrimaryTab}>
        <TabsList className="grid grid-cols-3 w-full" data-testid="primary-tabs">
          <TabsTrigger value="venue" className="flex items-center gap-2" data-testid="tab-venue">
            <Building2 className="w-4 h-4" />
            Venues ({(counts.pending || 0) + (counts.under_review || 0)})
          </TabsTrigger>
          <TabsTrigger value="sp" className="flex items-center gap-2" data-testid="tab-sp">
            <Dumbbell className="w-4 h-4" />
            Sports Professionals
          </TabsTrigger>
          <TabsTrigger value="rejected" className="flex items-center gap-2" data-testid="tab-rejected">
            <XCircle className="w-4 h-4" />
            Rejected ({rejectedVenues.length})
          </TabsTrigger>
        </TabsList>

        {/* ── VENUE TAB ── */}
        <TabsContent value="venue" className="mt-4 space-y-4">
          <AdminFilterBar
            searchValue={query}
            onSearchChange={setQuery}
            searchPlaceholder="Search venue, owner, city"
            searchTestId="venue-verification-search"
            filters={[
              { key: "status", label: "Status", value: statusFilter, onChange: setStatusFilter, options: allStatuses.map((status) => ({ value: status, label: status === "all" ? "All Statuses" : status })), testId: "venue-verification-status-filter" },
              { key: "business-type", label: "Business Type", value: businessTypeFilter, onChange: setBusinessTypeFilter, options: [{ value: "all", label: "All Business Types" }, ...businessTypes.map((type) => ({ value: type, label: type }))], testId: "venue-verification-business-type-filter" },
              { key: "sort", label: "Sort", value: sortOrder, onChange: setSortOrder, options: [{ value: "latest", label: "Latest First" }, { value: "oldest", label: "Oldest First" }], testId: "venue-verification-sort-filter" },
            ]}
            activeFilters={[
              query ? { key: "search", label: `Search: ${query}`, onRemove: () => setQuery("") } : null,
              statusFilter !== "all" ? { key: "status", label: `Status: ${statusFilter}`, onRemove: () => setStatusFilter("all") } : null,
              businessTypeFilter !== "all" ? { key: "business-type", label: `Business Type: ${businessTypeFilter}`, onRemove: () => setBusinessTypeFilter("all") } : null,
              sortOrder !== "latest" ? { key: "sort", label: "Sorted: oldest first", onRemove: () => setSortOrder("latest") } : null,
            ].filter(Boolean)}
            onClearAll={() => { setQuery(""); setStatusFilter("all"); setBusinessTypeFilter("all"); setSortOrder("latest"); }}
          />

          {/* Venue Sub-Tabs */}
          <Tabs value={venueSubTab} onValueChange={setVenueSubTab}>
            <TabsList className="grid grid-cols-3 w-full max-w-lg">
              <TabsTrigger value="queue" data-testid="venue-subtab-queue">Queue ({filteredQueue.length})</TabsTrigger>
              <TabsTrigger value="verified" data-testid="venue-subtab-verified">Verified ({counts.verified || 0})</TabsTrigger>
              <TabsTrigger value="all-venues" data-testid="venue-subtab-all">All Venues ({allVenues.length})</TabsTrigger>
            </TabsList>

            {/* Queue */}
            <TabsContent value="queue" className="space-y-3 mt-4">
              {filteredQueue.length === 0 ? (
                <Card><CardContent className="p-8 text-center text-muted-foreground">No venues pending verification</CardContent></Card>
              ) : filteredQueue.map(v => (
                <Card key={v.venue_id} className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => openDetail(v.venue_id, true)} data-testid={`queue-venue-${v.venue_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading text-sm font-bold uppercase">{v.name}</h3>
                          <Badge className={STATUS_STYLES[v.ownership_verification_status] || "bg-muted"}>{v.ownership_verification_status}</Badge>
                          {v.duplicate_risk_score >= 50 && <Badge variant="destructive">DUP RISK: {v.duplicate_risk_score}</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">{v.city} · {v.business_type || "N/A"} · Level {v.verification_level || 0}</p>
                        <p className="text-xs text-muted-foreground">Submitted: {v.verification_submitted_at?.slice(0, 10) || "N/A"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Risk</p>
                          <p className={`text-lg font-heading font-bold ${v.venue_risk_score > 50 ? "text-red-500" : v.venue_risk_score > 25 ? "text-amber-500" : "text-green-600"}`}>{v.venue_risk_score || 0}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* Verified */}
            <TabsContent value="verified" className="mt-4">
              {filteredVerified.length === 0 ? (
                <Card><CardContent className="p-6 text-center text-muted-foreground"><CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" /><p>No verified venues match current filters</p></CardContent></Card>
              ) : (
                <div className="space-y-3">
                  {filteredVerified.map((venue) => (
                    <Card key={venue.venue_id} className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => { setSelectedVenueId(venue.venue_id); openDetail(venue.venue_id, true); }} data-testid={`verified-venue-${venue.venue_id}`}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{venue.name}</p>
                          <p className="text-xs text-muted-foreground">{venue.owner_name || "—"} · {venue.city || "—"} · Approved {venue.verified_at?.slice(0, 10) || "—"}</p>
                        </div>
                        <div className="text-right text-sm">
                          <p className="font-medium">{venue.courts_count} courts</p>
                          <p className="text-xs text-muted-foreground">{venue.bookings_count} bookings</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* All Venues */}
            <TabsContent value="all-venues" className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-6 border border-zinc-200 bg-white">
                <div className="lg:col-span-8 border-r border-zinc-200 overflow-x-auto">
                  <table className="w-full min-w-[900px] text-sm" data-testid="all-venues-table">
                    <thead>
                      <tr className="border-b text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground bg-slate-50">
                        <th className="py-3 px-4">Venue</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4">Created</th>
                        <th className="py-3 px-4">Approved</th>
                        <th className="py-3 px-4">Owner</th>
                        <th className="py-3 px-4">City</th>
                        <th className="py-3 px-4">Courts</th>
                        <th className="py-3 px-4">Bookings</th>
                        <th className="py-3 px-4">Last Booking</th>
                        <th className="py-3 px-4">Earnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAllVenues.map((venue) => (
                        <tr key={venue.venue_id} className={`border-b hover:bg-zinc-50 cursor-pointer ${selectedVenueId === venue.venue_id ? "bg-zinc-50" : ""}`} onClick={() => { setSelectedVenueId(venue.venue_id); openDetail(venue.venue_id, false); }} data-testid={`all-venue-row-${venue.venue_id}`}>
                          <td className="py-3 px-4 font-medium">{venue.name}</td>
                          <td className="py-3 px-4"><Badge className={STATUS_STYLES[venue.status] || "bg-muted"}>{venue.status}</Badge></td>
                          <td className="py-3 px-4">{venue.created_at?.slice(0, 10) || "—"}</td>
                          <td className="py-3 px-4">{venue.verified_at?.slice(0, 10) || "—"}</td>
                          <td className="py-3 px-4">{venue.owner_name || "—"}</td>
                          <td className="py-3 px-4">{venue.city || "—"}</td>
                          <td className="py-3 px-4">{venue.courts_count}</td>
                          <td className="py-3 px-4">{venue.bookings_count}</td>
                          <td className="py-3 px-4">{venue.last_booking_at?.slice(0, 10) || "—"}</td>
                          <td className="py-3 px-4">₱{Number(venue.total_earnings || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="lg:col-span-4">
                  <AdminDetailPanel title={detail?.venue?.name} subtitle={detail?.owner ? `${detail.owner.name} · ${detail.owner.email}` : "Select any venue to inspect its 360° view"} testId="venue-360-panel">
                    {detail ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-muted-foreground">Status:</span> {detail.venue?.ownership_verification_status}</div>
                          <div><span className="text-muted-foreground">Level:</span> {detail.venue?.verification_level}</div>
                          <div><span className="text-muted-foreground">City:</span> {detail.venue?.city || "—"}</div>
                          <div><span className="text-muted-foreground">Created:</span> {detail.venue?.created_at?.slice(0, 10) || "—"}</div>
                          <div><span className="text-muted-foreground">Courts:</span> {detail.stats?.courts_count || 0}</div>
                          <div><span className="text-muted-foreground">Bookings:</span> {detail.stats?.bookings_count || 0}</div>
                          <div><span className="text-muted-foreground">Last Booking:</span> {detail.stats?.last_booking_at?.slice(0, 10) || "—"}</div>
                          <div><span className="text-muted-foreground">Earnings:</span> ₱{Number(detail.stats?.total_earnings || 0).toLocaleString()}</div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/venues/${detail.venue.venue_id}/360`)} data-testid="open-venue-360-dialog-btn">Full 360 View</Button>
                          {detail.venue?.ownership_verification_status === "rejected" && (
                            <Button size="sm" variant="outline" onClick={() => deleteRejectedVenue(detail.venue.venue_id)} className="text-red-600 border-red-200" data-testid="delete-rejected-venue-btn"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </AdminDetailPanel>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ── SP TAB ── */}
        <TabsContent value="sp" className="mt-4">
          <SPVerificationTab onCountUpdate={setSpCount} />
        </TabsContent>

        {/* ── REJECTED TAB ── */}
        <TabsContent value="rejected" className="mt-4">
          <RejectedVerificationsTab
            rejectedVenues={rejectedVenues}
            onDeleteVenue={deleteRejectedVenue}
            onViewVenue={(venueId) => openDetail(venueId, true)}
          />
        </TabsContent>
      </Tabs>

      {/* Detail Dialog (for Venue review) */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase">Venue Verification Review</DialogTitle>
            <DialogDescription className="sr-only">Review venue documents and take approval actions</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              {/* Venue Info */}
              <Card>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold">{detail.venue?.name}</h3>
                    <Badge className={STATUS_STYLES[detail.venue?.ownership_verification_status] || "bg-muted"}>{detail.venue?.ownership_verification_status}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Business Type:</span> {detail.venue?.business_type || "N/A"}</div>
                    <div><span className="text-muted-foreground">Registration #:</span> {detail.venue?.business_registration_number || "N/A"}</div>
                    <div><span className="text-muted-foreground">Entity:</span> {detail.venue?.legal_entity_name || "N/A"}</div>
                    <div><span className="text-muted-foreground">Signatory:</span> {detail.venue?.authorized_signatory_name || "N/A"}</div>
                    <div><span className="text-muted-foreground">Bank:</span> {detail.venue?.bank_name || "N/A"} - {detail.venue?.bank_account_number || "N/A"}</div>
                    <div><span className="text-muted-foreground">Payout:</span> <Badge className={detail.venue?.payout_status === "unlocked" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>{detail.venue?.payout_status || "locked"}</Badge></div>
                  </div>
                </CardContent>
              </Card>

              {/* Duplicate Detection */}
              <Card>
                <CardHeader><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Duplicate Detection</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-2xl font-heading font-bold">{detail.duplicate_detection?.duplicate_risk_score}</span>
                    <span className="text-sm text-muted-foreground">/ 100 risk</span>
                    {detail.duplicate_detection?.blocked && <Badge variant="destructive">BLOCKED</Badge>}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    {["name", "geo", "address", "image"].map(k => (
                      <div key={k} className={`p-2 rounded ${detail.duplicate_detection?.flags?.[k] ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"}`}>
                        <p className="text-xs font-medium capitalize">{k}</p>
                        <p className="text-sm font-bold">{detail.duplicate_detection?.scores?.[k] || 0}%</p>
                      </div>
                    ))}
                  </div>
                  {(detail.duplicate_detection?.similar_venues || []).length > 0 && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs font-medium">Similar Venues:</p>
                      {detail.duplicate_detection.similar_venues.map((sv, i) => (
                        <p key={i} className="text-xs text-muted-foreground">• {sv.name} (similarity: {sv.total_similarity}%)</p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Level-by-Level Review */}
              <Card>
                <CardHeader><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Level-by-Level Review</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(detail.level_sections || []).map(section => (
                      <div key={section.level} className={`rounded-lg border p-3 ${LEVEL_STATUS_STYLES[section.status] || LEVEL_STATUS_STYLES.pending}`} data-testid={`level-status-${section.level}`}>
                        <p className="text-xs font-heading uppercase tracking-wide">Level {section.level}</p>
                        <p className="font-medium text-sm mt-1">{section.name}</p>
                        <p className="text-xs mt-1 opacity-80">{section.description}</p>
                      </div>
                    ))}
                  </div>

                  {(detail.level_sections || []).map(section => (
                    <div key={`section-${section.level}`} className="rounded-lg border p-4 space-y-3" data-testid={`level-section-${section.level}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h4 className="font-medium">Level {section.level}: {section.name}</h4>
                          <p className="text-xs text-muted-foreground">{section.description}</p>
                        </div>
                        <Badge className={section.status === "approved" ? "bg-green-100 text-green-800" : section.status === "current" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}>
                          {section.status}
                        </Badge>
                      </div>

                      {section.documents.length === 0 && (section.supplemental || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No supporting documents uploaded for this level yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {section.documents.map((doc) => {
                            const fileUrl = resolveUploadUrl(doc.file_url);
                            return (
                              <div key={doc.document_id} className="rounded-lg border p-3 bg-white">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-medium">{doc.label}</p>
                                    <p className="text-xs text-muted-foreground">{doc.file_name || doc.doc_type}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-[10px]">{doc.status}</Badge>
                                    {fileUrl && (
                                      <Button size="sm" variant="outline" onClick={() => window.open(fileUrl, "_blank")} data-testid={`view-doc-${doc.document_id}`}>
                                        <Eye className="w-3 h-3 mr-1" />View
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                {fileUrl && isImagePreview(fileUrl) && (
                                  <img src={fileUrl} alt={doc.label} className="mt-3 max-h-48 rounded border object-contain" onError={(e) => (e.target.style.display = "none")} />
                                )}
                              </div>
                            );
                          })}

                          {(section.supplemental || []).map((item, idx) => (
                            <div key={`${section.level}-supp-${idx}`} className="rounded-lg border border-dashed p-3 bg-slate-50">
                              <p className="text-xs font-heading uppercase tracking-wide text-muted-foreground">Supporting Evidence</p>
                              <p className="text-sm font-medium mt-1">{item.label}</p>
                              <p className="text-xs text-muted-foreground mt-1">{item.value}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Admin Actions */}
              <Card>
                <CardHeader><CardTitle className="text-sm">Take Action</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-lg border bg-slate-50 p-3" data-testid="current-review-level-card">
                    <p className="text-xs font-heading uppercase tracking-wide text-muted-foreground">Current Review Level</p>
                    <p className="font-medium mt-1">Level {actionLevel}: {LEVEL_NAMES[parseInt(actionLevel, 10)]}</p>
                    <p className="text-xs text-muted-foreground mt-1">Approving this step keeps you on the same screen and moves the review to the next level automatically.</p>
                  </div>
                  <Textarea placeholder="Reason / notes..." value={actionReason} onChange={e => setActionReason(e.target.value)} className="text-sm" />
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => takeAction(detail.venue?.venue_id, "approve")} className="bg-green-600 hover:bg-green-700 text-white" data-testid="venue-approve-btn">
                      <CheckCircle className="w-3 h-3 mr-1" />Approve Level {actionLevel}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => takeAction(detail.venue?.venue_id, "reject")} data-testid="venue-reject-btn">
                      <XCircle className="w-3 h-3 mr-1" />Reject
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => takeAction(detail.venue?.venue_id, "suspend")} data-testid="venue-suspend-btn">
                      <Lock className="w-3 h-3 mr-1" />Suspend
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => takeAction(detail.venue?.venue_id, "request_docs")} data-testid="venue-request-docs-btn">
                      <FileText className="w-3 h-3 mr-1" />Request Docs
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Audit Trail */}
              {(detail.audit_trail || []).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-sm">Audit Trail</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {detail.audit_trail.map(a => (
                        <div key={a.log_id} className="flex items-center justify-between py-1 text-xs border-b last:border-0">
                          <span>{a.action} by {a.admin_name}</span>
                          <span className="text-muted-foreground">{a.timestamp?.slice(0, 16).replace("T", " ")}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
